import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAVY = "#000000";
const ROSE = "#666666";
const CREAM = "#FFFFFF";
const PAPER = "#FFFFFF";
const HAIR = "#000000";
const DISPLAY = "'Archivo Black', 'Inter', system-ui, sans-serif";
const BODY = "'Inter', system-ui, -apple-system, sans-serif";

function getToken() {
  return sessionStorage.getItem("admin_token") || "";
}
function setToken(t) {
  if (t) sessionStorage.setItem("admin_token", t);
  else sessionStorage.removeItem("admin_token");
}

export default function AdminApp() {
  const [token, setTok] = useState(getToken());
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [feedbackUnread, setFeedbackUnread] = useState(0);
  const [bench, setBench] = useState([]);
  const [benchPending, setBenchPending] = useState(0);
  const [bookings, setBookings] = useState([]);
  const [bookingsPaid, setBookingsPaid] = useState(0);
  const [tab, setTab] = useState("users"); // 'users' | 'feedback' | 'bench' | 'bookings'
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState("");

  async function loadBench(overrideToken) {
    const t = overrideToken || token;
    if (!t) return;
    try {
      const { data } = await axios.get(`${API}/admin/bench`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setBench(data.experts || []);
      setBenchPending(data.pending || 0);
    } catch (e) {
      console.warn("admin: failed to load bench", e);
    }
  }

  async function setExpertStatus(eid, status) {
    try {
      const { data } = await axios.post(
        `${API}/admin/bench/${eid}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBench((b) => b.map((x) => (x.id === eid ? data.expert : x)));
      // Refresh pending count
      await loadBench();
    } catch (e) {
      console.warn("setExpertStatus failed", e);
    }
  }

  async function deleteExpert(eid) {
    if (!window.confirm("Delete this application?")) return;
    try {
      await axios.delete(`${API}/admin/bench/${eid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBench((b) => b.filter((x) => x.id !== eid));
      await loadBench();
    } catch (e) {
      console.warn("deleteExpert failed", e);
    }
  }

  async function updateExpert(eid, patch) {
    try {
      const { data } = await axios.patch(
        `${API}/admin/bench/${eid}`,
        patch,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBench((b) => b.map((x) => (x.id === eid ? data.expert : x)));
      return true;
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't update expert.");
      return false;
    }
  }

  async function loadBookings(overrideToken) {
    const t = overrideToken || token;
    if (!t) return;
    try {
      const { data } = await axios.get(`${API}/admin/bench/bookings`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setBookings(data.bookings || []);
      setBookingsPaid(data.paid_count || 0);
    } catch (e) {
      console.warn("admin: failed to load bookings", e);
    }
  }

  async function loadFeedback(overrideToken) {
    const t = overrideToken || token;
    if (!t) return;
    try {
      const { data } = await axios.get(`${API}/admin/feedback`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setFeedback(data.feedback || []);
      setFeedbackUnread(data.unread || 0);
    } catch (e) {
      console.warn("admin: failed to load feedback", e);
    }
  }

  async function markFeedbackRead(fid) {
    try {
      await axios.post(
        `${API}/admin/feedback/${fid}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeedback((f) => f.map((x) => (x.id === fid ? { ...x, read: true } : x)));
      setFeedbackUnread((c) => Math.max(0, c - 1));
    } catch (e) {
      console.warn("markFeedbackRead failed", e);
    }
  }

  async function deleteFeedback(fid) {
    if (!window.confirm("Delete this feedback?")) return;
    try {
      await axios.delete(`${API}/admin/feedback/${fid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFeedback((f) => f.filter((x) => x.id !== fid));
    } catch (e) {
      console.warn("deleteFeedback failed", e);
    }
  }

  async function load(overrideToken) {
    const t = overrideToken || token;
    if (!t) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setUsers(data.users);
    } catch (e) {
      if (e.response && e.response.status === 401 && !overrideToken) {
        setToken("");
        setTok("");
      } else {
        console.error("admin: failed to load users", e);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    loadFeedback();
    loadBench();
    loadBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function login(e) {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, creds);
      setToken(data.token);
      setTok(data.token);
      await load(data.token); // load with the fresh token, bypassing race
      await loadFeedback(data.token);
      await loadBench(data.token);
      await loadBookings(data.token);
    } catch {
      setError("Invalid credentials.");
    }
  }

  async function comp(uid) {
    setActionBusy(uid + ":comp");
    try {
      await axios.post(
        `${API}/admin/users/${uid}/comp`,
        { days: 30 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await load();
    } finally {
      setActionBusy("");
    }
  }
  async function forceReaccept(uid) {
    setActionBusy(uid + ":fr");
    try {
      await axios.post(
        `${API}/admin/users/${uid}/force-reacceptance`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await load();
    } finally {
      setActionBusy("");
    }
  }
  async function del(uid) {
    if (!window.confirm("Permanently delete this user and all their data?")) return;
    setActionBusy(uid + ":del");
    try {
      await axios.delete(`${API}/admin/users/${uid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await load();
    } finally {
      setActionBusy("");
    }
  }
  async function logout() {
    try {
      await axios.post(`${API}/admin/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {
      console.warn("admin logout failed (non-fatal)", e);
    }
    setToken("");
    setTok("");
  }

  if (!token) {
    return (
      <div
        data-testid="admin-login"
        style={{
          minHeight: "100vh",
          background: CREAM,
          color: NAVY,
          fontFamily: BODY,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <form onSubmit={login} style={{ maxWidth: 380, width: "100%" }}>
          <h1
            style={{
              fontFamily: DISPLAY,
              fontSize: 36,
              margin: "0 0 18px",
              fontWeight: 400,
              textAlign: "center",
            }}
          >
            Admin
          </h1>
          <input
            data-testid="admin-username"
            placeholder="Username"
            value={creds.username}
            onChange={(e) => setCreds((c) => ({ ...c, username: e.target.value }))}
            style={{
              width: "100%",
              border: `1px solid ${HAIR}`,
              borderRadius: 10,
              padding: "12px 14px",
              fontFamily: BODY,
              fontSize: 14,
              background: PAPER,
              color: NAVY,
              outline: "none",
              marginBottom: 10,
              boxSizing: "border-box",
            }}
          />
          <input
            data-testid="admin-password"
            type="password"
            placeholder="Password"
            value={creds.password}
            onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))}
            style={{
              width: "100%",
              border: `1px solid ${HAIR}`,
              borderRadius: 10,
              padding: "12px 14px",
              fontFamily: BODY,
              fontSize: 14,
              background: PAPER,
              color: NAVY,
              outline: "none",
              marginBottom: 14,
              boxSizing: "border-box",
            }}
          />
          {error && (
            <p style={{ color: ROSE, fontSize: 13, marginTop: 0, marginBottom: 10 }}>{error}</p>
          )}
          <button
            data-testid="admin-login-btn"
            type="submit"
            style={{
              width: "100%",
              border: "none",
              cursor: "pointer",
              background: NAVY,
              color: CREAM,
              borderRadius: 10,
              padding: "12px 16px",
              fontFamily: DISPLAY,
              fontSize: 15,
            }}
          >
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div
      data-testid="admin-dashboard"
      style={{
        minHeight: "100vh",
        background: CREAM,
        color: NAVY,
        fontFamily: BODY,
        padding: "26px 30px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <h1 style={{ fontFamily: DISPLAY, fontSize: 30, margin: 0, fontWeight: 400 }}>
          Admin
        </h1>
        <button
          data-testid="admin-logout-btn"
          onClick={logout}
          style={{
            border: `1px solid ${HAIR}`,
            background: "transparent",
            cursor: "pointer",
            color: NAVY,
            padding: "8px 14px",
            borderRadius: 10,
            fontFamily: BODY,
            fontSize: 13,
          }}
        >
          Sign out
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 18, borderBottom: `2px solid ${HAIR}` }}>
        {[
          { id: "users", label: `Users (${users.length})` },
          {
            id: "feedback",
            label: feedbackUnread > 0 ? `Feedback (${feedbackUnread} new)` : `Feedback (${feedback.length})`,
          },
          {
            id: "bench",
            label: benchPending > 0 ? `Bench (${benchPending} pending)` : `Bench (${bench.length})`,
          },
          {
            id: "bookings",
            label: `Bookings (${bookings.length})`,
          },
        ].map((t) => {
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              style={{
                border: "none",
                background: isActive ? NAVY : "transparent",
                color: isActive ? CREAM : NAVY,
                cursor: "pointer",
                padding: "12px 22px",
                fontFamily: BODY,
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {loading && tab === "users" && <p style={{ color: "rgba(0,0,0,0.6)" }}>Loading…</p>}

      {tab === "feedback" && (
        <div data-testid="admin-feedback-list" style={{ marginBottom: 30 }}>
          {feedback.length === 0 ? (
            <p style={{ color: "rgba(0,0,0,0.6)", fontWeight: 600 }}>No feedback yet.</p>
          ) : (
            feedback.map((it) => (
              <div
                key={it.id}
                data-testid={`feedback-${it.id}`}
                style={{
                  border: `2px solid ${HAIR}`,
                  background: it.read ? PAPER : "#FFF8DC",
                  padding: "18px 22px",
                  marginBottom: 12,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 8 }}>
                  <div>
                    <span style={{ fontFamily: DISPLAY, fontSize: 16, marginRight: 10 }}>
                      {it.name || "Anonymous"}
                    </span>
                    {it.email && (
                      <a
                        href={`mailto:${it.email}?subject=Re%3A%20Hey%20Sova`}
                        style={{ color: NAVY, fontSize: 13, fontWeight: 700, textDecoration: "underline" }}
                      >
                        {it.email}
                      </a>
                    )}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(0,0,0,0.55)" }}>
                    {new Date(it.submitted_at).toLocaleString()}
                  </div>
                </div>
                <p style={{ fontSize: 14, color: NAVY, fontWeight: 500, lineHeight: 1.5, whiteSpace: "pre-wrap", margin: "8px 0 14px" }}>
                  {it.message}
                </p>
                <div style={{ display: "flex", gap: 8 }}>
                  {!it.read && (
                    <button
                      onClick={() => markFeedbackRead(it.id)}
                      style={{
                        border: `2px solid ${HAIR}`,
                        background: NAVY,
                        color: CREAM,
                        cursor: "pointer",
                        padding: "8px 14px",
                        fontFamily: BODY,
                        fontWeight: 800,
                        fontSize: 11,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      Mark read
                    </button>
                  )}
                  <button
                    onClick={() => deleteFeedback(it.id)}
                    style={{
                      border: `2px solid ${HAIR}`,
                      background: PAPER,
                      color: NAVY,
                      cursor: "pointer",
                      padding: "8px 14px",
                      fontFamily: BODY,
                      fontWeight: 800,
                      fontSize: 11,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "bench" && (
        <div data-testid="admin-bench-list" style={{ marginBottom: 30 }}>
          {bench.length === 0 ? (
            <p style={{ color: "rgba(0,0,0,0.6)", fontWeight: 600 }}>
              No applications yet. Share <code>https://heysova.io/apply-to-bench</code> with executives.
            </p>
          ) : (
            bench.map((ex) => {
              const tint =
                ex.status === "pending"
                  ? "#FFF8DC"
                  : ex.status === "approved"
                  ? "#F0FFF4"
                  : "#FFF0F0";
              return (
                <div
                  key={ex.id}
                  data-testid={`bench-${ex.id}`}
                  style={{
                    border: `2px solid ${HAIR}`,
                    background: tint,
                    padding: "18px 22px",
                    marginBottom: 12,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 6 }}>
                    <div>
                      <span style={{ fontFamily: DISPLAY, fontSize: 18, marginRight: 10 }}>
                        {ex.name}
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", border: `2px solid ${HAIR}`, padding: "2px 8px", marginRight: 6 }}>
                        {ex.status}
                      </span>
                      {ex.ai_score && ex.ai_score !== "unscored" && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            padding: "2px 8px",
                            background:
                              ex.ai_score === "high"
                                ? "#000000"
                                : ex.ai_score === "flag"
                                ? "#FFE0E0"
                                : "#FFF6D6",
                            color: ex.ai_score === "high" ? "#FFFFFF" : "#000000",
                            border: `2px solid ${HAIR}`,
                          }}
                          title={ex.ai_notes || ""}
                        >
                          AI · {ex.ai_score}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(0,0,0,0.55)" }}>
                      {new Date(ex.submitted_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(0,0,0,0.7)", marginBottom: 10 }}>
                    {ex.role}
                  </div>
                  {ex.ai_notes && (
                    <div style={{ fontSize: 12, fontStyle: "italic", color: "rgba(0,0,0,0.7)", borderLeft: `3px solid ${HAIR}`, paddingLeft: 10, marginBottom: 10, fontWeight: 500 }}>
                      AI: {ex.ai_notes}
                    </div>
                  )}
                  <p style={{ fontSize: 14, color: NAVY, fontWeight: 500, lineHeight: 1.5, whiteSpace: "pre-wrap", margin: "8px 0 12px" }}>
                    {ex.bio}
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 14, fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,0.75)", alignItems: "center" }}>
                    <span>📧 <a href={`mailto:${ex.email}`} style={{ color: NAVY, textDecoration: "underline" }}>{ex.email}</a></span>
                    {ex.linkedin_url && (
                      <span>
                        🔗{" "}
                        <a href={ex.linkedin_url} target="_blank" rel="noreferrer" style={{ color: NAVY, textDecoration: "underline" }}>
                          LinkedIn
                        </a>
                      </span>
                    )}
                    {ex.cal_link && (
                      <span>
                        🗓{" "}
                        <a href={ex.cal_link} target="_blank" rel="noreferrer" style={{ color: NAVY, textDecoration: "underline" }}>
                          Cal.com
                        </a>
                      </span>
                    )}
                    {ex.hourly_rate_usd ? <span>💵 ${ex.hourly_rate_usd}/hr</span> : null}
                    <span data-testid={`bench-price-${ex.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      🎟 Session $
                      <input
                        type="number"
                        min="0"
                        defaultValue={ex.session_price_usd || ""}
                        placeholder="—"
                        data-testid={`bench-price-input-${ex.id}`}
                        onBlur={(e) => {
                          const v = parseInt(e.target.value, 10);
                          const next = Number.isFinite(v) && v >= 0 ? v : 0;
                          if ((ex.session_price_usd || 0) !== next) {
                            updateExpert(ex.id, { session_price_usd: next });
                          }
                        }}
                        style={{
                          width: 70,
                          border: `2px solid ${HAIR}`,
                          padding: "3px 6px",
                          fontFamily: BODY,
                          fontWeight: 800,
                          fontSize: 12,
                          background: PAPER,
                          color: NAVY,
                        }}
                      />
                    </span>
                    {ex.availability_note && <span>{ex.availability_note}</span>}
                  </div>
                  {ex.expertise && ex.expertise.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
                      {ex.expertise.map((t) => (
                        <span
                          key={t}
                          style={{
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            border: `1.5px solid ${HAIR}`,
                            padding: "3px 8px",
                          }}
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {ex.status !== "approved" && (
                      <button
                        onClick={() => setExpertStatus(ex.id, "approved")}
                        data-testid={`bench-approve-${ex.id}`}
                        style={{
                          border: `2px solid ${HAIR}`,
                          background: NAVY,
                          color: CREAM,
                          cursor: "pointer",
                          padding: "8px 14px",
                          fontFamily: BODY,
                          fontWeight: 800,
                          fontSize: 11,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                        }}
                      >
                        Approve
                      </button>
                    )}
                    {ex.status !== "rejected" && (
                      <button
                        onClick={() => setExpertStatus(ex.id, "rejected")}
                        style={{
                          border: `2px solid ${HAIR}`,
                          background: PAPER,
                          color: NAVY,
                          cursor: "pointer",
                          padding: "8px 14px",
                          fontFamily: BODY,
                          fontWeight: 800,
                          fontSize: 11,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                        }}
                      >
                        Reject
                      </button>
                    )}
                    {ex.status === "approved" && (
                      <button
                        onClick={() => setExpertStatus(ex.id, "pending")}
                        style={{
                          border: `2px solid ${HAIR}`,
                          background: PAPER,
                          color: NAVY,
                          cursor: "pointer",
                          padding: "8px 14px",
                          fontFamily: BODY,
                          fontWeight: 800,
                          fontSize: 11,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                        }}
                      >
                        Move to pending
                      </button>
                    )}
                    <button
                      onClick={() => deleteExpert(ex.id)}
                      style={{
                        border: `2px solid ${HAIR}`,
                        background: PAPER,
                        color: NAVY,
                        cursor: "pointer",
                        padding: "8px 14px",
                        fontFamily: BODY,
                        fontWeight: 800,
                        fontSize: 11,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {tab === "bookings" && (
        <div data-testid="admin-bookings-list" style={{ marginBottom: 30 }}>
          <div style={{ display: "flex", gap: 18, marginBottom: 14, fontSize: 12, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(0,0,0,0.7)" }}>
            <span>{bookings.length} total</span>
            <span>{bookingsPaid} paid</span>
          </div>
          {bookings.length === 0 ? (
            <p style={{ color: "rgba(0,0,0,0.6)", fontWeight: 600 }}>
              No bookings yet. Once a visitor pays to book an expert, you'll see it here.
            </p>
          ) : (
            <div style={{ overflowX: "auto", border: `2px solid ${HAIR}`, background: PAPER }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: CREAM, textAlign: "left" }}>
                    {["Date", "Status", "Amount", "Expert", "Booked by", "Cal.com"].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "10px 12px",
                          fontFamily: DISPLAY,
                          fontWeight: 600,
                          borderBottom: `2px solid ${HAIR}`,
                          fontSize: 11,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: NAVY,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((bk) => (
                    <tr key={bk.id} data-testid={`booking-${bk.id}`} style={{ borderTop: `1px solid ${HAIR}` }}>
                      <td style={{ padding: "10px 12px", color: "rgba(0,0,0,0.7)" }}>
                        {bk.created_at ? new Date(bk.created_at).toLocaleString() : "—"}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "3px 10px",
                            border: `2px solid ${HAIR}`,
                            background: bk.status === "paid" ? NAVY : PAPER,
                            color: bk.status === "paid" ? CREAM : NAVY,
                            fontSize: 11,
                            fontWeight: 800,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                          }}
                        >
                          {bk.status || "pending"}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px", fontWeight: 800 }}>
                        ${bk.amount_usd}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ fontWeight: 700 }}>{bk.expert_name}</div>
                        <div style={{ fontSize: 11, color: "rgba(0,0,0,0.6)" }}>{bk.expert_email}</div>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ fontWeight: 700 }}>{bk.user_name}</div>
                        <a href={`mailto:${bk.user_email}`} style={{ fontSize: 11, color: NAVY, textDecoration: "underline" }}>
                          {bk.user_email}
                        </a>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        {bk.cal_link ? (
                          <a href={bk.cal_link} target="_blank" rel="noreferrer" style={{ color: NAVY, textDecoration: "underline", fontSize: 12 }}>
                            Open ↗
                          </a>
                        ) : (
                          <span style={{ color: "rgba(0,0,0,0.4)", fontSize: 11 }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "users" && (
      <div style={{ overflowX: "auto", border: `1px solid ${HAIR}`, borderRadius: 12, background: PAPER }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: CREAM, textAlign: "left" }}>
              {["Email", "Joined", "Subscription", "Convos", "Boardrooms", "Open milestones", "Policy", "Actions"].map(
                (h) => (
                  <th
                    key={h}
                    style={{
                      padding: "10px 12px",
                      fontFamily: DISPLAY,
                      fontWeight: 600,
                      borderBottom: `1px solid ${HAIR}`,
                      fontSize: 12,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: "rgba(0,0,0,0.7)",
                    }}
                  >
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} data-testid={`admin-user-${u.user_id}`} style={{ borderTop: `1px solid ${HAIR}` }}>
                <td style={{ padding: "10px 12px" }}>{u.email}</td>
                <td style={{ padding: "10px 12px", color: "rgba(0,0,0,0.7)" }}>
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td style={{ padding: "10px 12px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "3px 10px",
                      border: `2px solid ${NAVY}`,
                      background: u.subscription_active ? NAVY : CREAM,
                      color: u.subscription_active ? CREAM : NAVY,
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                    }}
                  >
                    {u.subscription_active ? "ACTIVE" : "NONE"}
                  </span>
                  {u.subscription_until && (
                    <span style={{ marginLeft: 6, fontSize: 11, color: "rgba(0,0,0,0.55)" }}>
                      until {new Date(u.subscription_until).toLocaleDateString()}
                    </span>
                  )}
                </td>
                <td style={{ padding: "10px 12px" }}>{u.conversations}</td>
                <td style={{ padding: "10px 12px" }}>{u.boardroom_sessions}</td>
                <td style={{ padding: "10px 12px" }}>{u.open_milestones}</td>
                <td style={{ padding: "10px 12px", fontSize: 11, color: "rgba(0,0,0,0.6)" }}>
                  {u.policy_version || "—"}
                </td>
                <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                  <button
                    data-testid={`admin-comp-${u.user_id}`}
                    onClick={() => comp(u.user_id)}
                    disabled={actionBusy === u.user_id + ":comp"}
                    style={btn(NAVY, CREAM)}
                  >
                    Comp 30d
                  </button>{" "}
                  <button
                    data-testid={`admin-fr-${u.user_id}`}
                    onClick={() => forceReaccept(u.user_id)}
                    disabled={actionBusy === u.user_id + ":fr"}
                    style={btn("transparent", NAVY, true)}
                  >
                    Force re-accept
                  </button>{" "}
                  <button
                    data-testid={`admin-del-${u.user_id}`}
                    onClick={() => del(u.user_id)}
                    disabled={actionBusy === u.user_id + ":del"}
                    style={btn("transparent", ROSE, true)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

function btn(bg, color, outlined) {
  return {
    border: outlined ? `1px solid ${HAIR}` : "none",
    background: bg,
    color,
    cursor: "pointer",
    padding: "6px 10px",
    borderRadius: 8,
    fontFamily: BODY,
    fontSize: 12,
  };
}
